import {
  Avatar,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AUFlagIcons,
  CloseDialogIcon,
  EditIcon,
  RemoveIcon,
} from "assets/styles/icons";
import { useProgress } from "context/ProgressProvider";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { StakeholderService } from "services/StakeholderService";
import {
  AppFormikCheckboxGroup,
  AppFormikTextField,
  AppPrimaryButton,
  AppSecondaryButton,
  BlackTooltip,
} from "shared/components/AppComponents";
import { abnSchema, DirectorsSchema } from "shared/helpers/Schema";
import {
  KYCStatusCode,
  KYCStatusColor,
  KYCStatusText,
  StakeholderRole,
} from "shared/helpers/Constants";
import Utils from "shared/helpers/Utils";
import { ProfileActions } from "state/profile/Action";
import { LoanService } from "services/LoanService";
import Toast from "shared/helpers/Toast";
import DeleteConfirmation from "../review-details/dialog/DeleteConfirmation";
import {
  Cancel,
  HourglassBottom,
  Unsubscribe,
  Verified,
} from "@mui/icons-material";

export const getRoleLabel = (roleValue: any): string => {
  if (typeof roleValue === "string") {
    return roleValue.charAt(0).toUpperCase() + roleValue.slice(1).toLowerCase();
  }
  const key = (Object.keys(StakeholderRole) as any[]).find(
    (k) => StakeholderRole[k] === roleValue,
  );

  if (!key) return "";
  return key.charAt(0) + key.slice(1).toLowerCase();
};

const Directors = () => {
  const { handleCompleteAndNext, currentProfile, isBroker } = useProgress();
  const dispatch = useDispatch();
  const [isInitiating, setIsInitiating] = useState(false);
  const [step, setStep] = useState(0);
  const [showDialogAdd, setShowDialogAdd] = useState(null);
  const [showDialogDone, setShowDialogDone] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedStakeholderId, setSelectedStakeholderId] = useState("");
  const [checkAgreementDirector, setCheckAgreementDirector] = useState(false);
  const [originalStakeholders, setOriginalStakeholders] = useState(
    currentProfile.stakeholders,
  );
  const [directorsLoader, setDirectorsLoader] = useState(true);
  const theme = useTheme();

  const role = (value: any): any => {
    const memberRole = value.memberRole;
    if (!memberRole || memberRole === "") {
      return [];
    }
    if (typeof memberRole === "string") {
      return [StakeholderRole[memberRole as keyof typeof StakeholderRole]];
    }
    if (Array.isArray(memberRole)) {
      return memberRole
        .map((role) => StakeholderRole[role as keyof typeof StakeholderRole])
        .filter((roleValue) => roleValue !== undefined);
    }
    return [];
  };

  const getInitialMemberRole = (
    role: number | number[] | undefined,
  ): string | string[] => {
    if (!role && role !== 0) return "";

    const roleToString = (numericRole: number): string => {
      return (
        (Object.keys(StakeholderRole) as (keyof typeof StakeholderRole)[]).find(
          (k) => StakeholderRole[k] === numericRole,
        ) || ""
      );
    };

    if (Array.isArray(role)) {
      const roleStrings = role.map(roleToString).filter((r) => r !== "");
      return roleStrings.length > 1 ? roleStrings : roleStrings[0] || "";
    }
    return roleToString(role);
  };

  const handleGetAllStakeholders = ({ isDelete = false } = {}) => {
    StakeholderService.getAllStakeholders(currentProfile.loadId)
      .then((response: any) => {
        console.log(response);
        let data = response.data.data.stakeholders;

        if (isDelete) {
          if (data?.filter((s) => s.kycStatus === 0).length < 1) {
            setShowDialogDone(false);
          }
        }
        dispatch(
          ProfileActions.setRndProfile({
            stakeholders: data,
          }),
        );
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setDirectorsLoader(false);
      });
  };

  const handleUpdateStakeholders = (id, value) => {
    StakeholderService.updateStakeholders(currentProfile.loadId, id, {
      firstName: value.memberFirstName,
      lastName: value.memberLastName,
      email: value.memberEmail,
      phoneNumber: value.memberPhoneNumber.replace(/\s+/g, ""),
      role: role(value),
      address: value.memberResidentialAddress,
      isExecutant: Array.isArray(role(value))
        ? role(value).includes(StakeholderRole.EXECUTANT)
        : role(value) === StakeholderRole.EXECUTANT,
    })
      .then((response: any) => {
        console.log(response);
        handleGetAllStakeholders();
        setShowDialogAdd(null);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {});
  };

  const handleSubmitForm = (value, formik = null, showDialogAdd) => {
    console.log(showDialogAdd);
    if (showDialogAdd && showDialogAdd !== "add") {
      handleUpdateStakeholders(showDialogAdd.stakeholderId, value);
      return;
    }
    console.log(value);

    StakeholderService.addStakeholders({
      loanId: currentProfile.loadId,
      stakeholders: [
        {
          firstName: value.memberFirstName,
          lastName: value.memberLastName,
          email: value.memberEmail,
          phoneNumber: value.memberPhoneNumber.replace(/\s+/g, ""),
          role: role(value),
          address: value.memberResidentialAddress,
          isExecutant: Array.isArray(role(value))
            ? role(value).includes(StakeholderRole.EXECUTANT)
            : role(value) === StakeholderRole.EXECUTANT,
        },
      ],
    })
      .then((response: any) => {
        handleGetAllStakeholders();
        setShowDialogAdd(null);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        formik?.setSubmitting(false);
      });
  };

  const handleDeleteStakeholder = () => {
    setIsDeleting(true);
    StakeholderService.deleteStakeholders(
      currentProfile.loadId,
      selectedStakeholderId,
    )
      .then((response: any) => {
        onCloseDeleteDialog();
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const initiateKYCMultiple = () => {
    setIsInitiating(true);
    LoanService.initiateKYCMultiple(currentProfile.loadId)
      .then((response: any) => {
        const data = response.data.data;
        console.log(data);
        setTimeout(() => {
          handleCompleteAndNext();
          setIsInitiating(false);
        }, 1000);
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsInitiating(false);
      });
  };

  let isExecutantExist = currentProfile.stakeholders.some((stakeholder) =>
    stakeholder.role.includes(StakeholderRole.EXECUTANT),
  );

  const actionOnConfirmDirectors = () => {
    if (isExecutantExist) {
      initiateKYCMultiple();
    } else {
      Toast.showErrorMessage(
        "At least one executant is required to process the loan",
      );
    }
  };

  useEffect(() => {
    handleGetAllStakeholders();
  }, []);

  const onCloseDeleteDialog = (): void => {
    setShowDeleteDialog(false);
    setSelectedStakeholderId("");
    handleGetAllStakeholders({ isDelete: true });
  };

  const isExecutantRoleExists = (): void => {
    if (isExecutantExist) {
      const allKycCompleted = currentProfile.stakeholders.every(
        (s: any) => s.kycStatus !== 0,
      );

      if (allKycCompleted) {
        handleCompleteAndNext();
        return;
      }
      setShowDialogDone(true);
    } else {
      Toast.showErrorMessage(
        "At least one executant is required to process the loan",
      );
    }
  };

  const HandleAddDirectorsForm = ({
    handleSubmitFormData,
    userInfoData,
    isDialog,
    showDialogAdd,
  }: any) => {
    const isEdit = showDialogAdd && showDialogAdd !== "add";

    return (
      <Box>
        <Formik
          enableReinitialize
          validationSchema={DirectorsSchema}
          validateOnMount
          onSubmit={(value, formik) => {
            formik.setSubmitting(true);
            handleSubmitFormData(value, formik, showDialogAdd);
          }}
          initialValues={{
            memberRole: isEdit ? getInitialMemberRole(showDialogAdd?.role) : "",
            memberFirstName:
              userInfoData.stakeholders.length < 1
                ? userInfoData.firstName
                : isEdit
                  ? showDialogAdd?.firstName
                  : "",
            memberLastName:
              userInfoData.stakeholders.length < 1
                ? userInfoData.lastName
                : isEdit
                  ? showDialogAdd?.lastName
                  : "",
            memberEmail:
              userInfoData.stakeholders.length < 1
                ? userInfoData.email
                : isEdit
                  ? showDialogAdd?.email
                  : "",
            memberPhoneNumber:
              userInfoData.stakeholders.length < 1
                ? userInfoData.phoneNumber
                : isEdit
                  ? showDialogAdd?.phoneNumber.startsWith("+61 ")
                    ? showDialogAdd?.phoneNumber
                    : showDialogAdd?.phoneNumber.startsWith("+61")
                      ? "+61 " + showDialogAdd?.phoneNumber.slice(3)
                      : showDialogAdd?.phoneNumber
                  : "+61 ",
            memberResidentialAddress: isEdit ? showDialogAdd?.address : "",
          }}
        >
          {({ handleSubmit, values, errors, isSubmitting }) => {
            return (
              <form autoComplete="off">
                {/* --- CHOOSE ROLE */}
                <Typography
                  variant="body1"
                  className="formik-text-field-container"
                  mt={3}
                >
                  Choose role <span>*</span>
                </Typography>

                <AppFormikCheckboxGroup
                  name="memberRole"
                  outerLabel={true}
                  options={[
                    { value: "DIRECTOR", label: "Director" },
                    { value: "SHAREHOLDER", label: "Shareholder" },
                    { value: "EXECUTANT", label: "Executant" },
                  ]}
                  readOnly={false}
                />

                {/* --- FORM */}
                <Grid container spacing={4} mt={3}>
                  <Grid size={{ xs: 12, md: 6 }} component="div">
                    <AppFormikTextField
                      placeholder="First name"
                      label={
                        <>
                          First name <span>*</span>
                        </>
                      }
                      name="memberFirstName"
                      outerLabel
                      className={`ezifin-default-field${
                        isDialog ? "-dialog" : ""
                      }`}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }} component="div">
                    <AppFormikTextField
                      placeholder="Last name"
                      label={
                        <>
                          Last name <span>*</span>
                        </>
                      }
                      name="memberLastName"
                      outerLabel
                      className={`ezifin-default-field${
                        isDialog ? "-dialog" : ""
                      }`}
                    />
                  </Grid>
                </Grid>
                <Box mt={3}>
                  <AppFormikTextField
                    placeholder="Email address"
                    label={
                      <>
                        Email address <span>*</span>
                      </>
                    }
                    name="memberEmail"
                    outerLabel
                    className={`ezifin-default-field${
                      isDialog ? "-dialog" : ""
                    }`}
                    blockedAndReadOnly={isEdit}
                  />
                </Box>

                <Box mt={3}>
                  <AppFormikTextField
                    placeholder="Phone number"
                    label={
                      <>
                        Phone number <span>*</span>
                      </>
                    }
                    name="memberPhoneNumber"
                    outerLabel
                    className={`ezifin-default-field${
                      isDialog ? "-dialog" : ""
                    }`}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <AUFlagIcons />
                          </InputAdornment>
                        ),
                      },
                    }}
                    type="customAUPhone"
                  />
                </Box>
                <Box mt={3}>
                  <AppFormikTextField
                    placeholder="Residential address"
                    label={
                      <>
                        Residential address <span>*</span>
                      </>
                    }
                    name="memberResidentialAddress"
                    outerLabel
                    className={`ezifin-default-field${
                      isDialog ? "-dialog" : ""
                    }`}
                    allowComma
                  />
                </Box>
                <Box mt={3}>
                  <AppSecondaryButton
                    title={isEdit ? "Save" : "Add"}
                    className="btn-small ezifin-default-outlined-button"
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    loadingText="Please wait.."
                    disabled={isSubmitting}
                  />
                </Box>

                {!isDialog && (
                  <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                    <AppPrimaryButton
                      title="Continue"
                      isLoading={false}
                      loadingText="Please wait.."
                      onClick={() => {}}
                      disabled={userInfoData.stakeholders.length < 1}
                    />
                  </Box>
                )}
              </form>
            );
          }}
        </Formik>
      </Box>
    );
  };

  // UI Elements
  return (
    <>
      {step === 0 && (
        <>
          <Box>
            <Typography variant="h2">
              {isBroker ? "Business details" : "Your business"}
            </Typography>
            <Typography variant="h3" mt={3}>
              Provide details of all the Directors related to the Business as
              well as any Shareholder <br />
              with &gt;25% ownership
            </Typography>
          </Box>
          {directorsLoader && (
            <Box className="empty-content" sx={{ height: "32vh" }}>
              <CircularProgress />
            </Box>
          )}
          {!directorsLoader && currentProfile.stakeholders.length < 1 && (
            <>
              <HandleAddDirectorsForm
                handleSubmitFormData={handleSubmitForm}
                userInfoData={currentProfile}
                isDialog={false}
              />
            </>
          )}
          {!directorsLoader && currentProfile.stakeholders.length > 0 && (
            <>
              <Box>
                <Typography variant="h3" mt={3} fontWeight={400}>
                  Total directors:
                </Typography>
                {currentProfile.stakeholders?.map((data, index) => (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      [theme.breakpoints.down("md")]: {
                        gap: "0.5rem",
                      },
                    }}
                  >
                    <Box
                      key={index}
                      mt={3}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        width: "380px",
                        border: "1px solid #E5E5E5",
                        borderRadius: "8px",
                        padding: "5px 16px",
                        justifyContent: "space-between",
                        [theme.breakpoints.down("md")]: {
                          width: "unset",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <Avatar
                          sx={{
                            background:
                              "linear-gradient(257.91deg, #0575E6 -9.32%, #021B79 78.43%, #000000 177.87%)",
                          }}
                        >
                          {data.firstName?.slice(0, 1)}
                          {data.lastName?.slice(0, 1)}
                        </Avatar>
                        <Box>
                          <Typography variant="h3" fontWeight={400}>
                            {data.firstName} {data.lastName}
                          </Typography>
                          <Typography
                            variant="h4"
                            fontWeight={400}
                            sx={{ marginTop: "-5px" }}
                          >
                            {getRoleLabel(data.role)}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex" }}>
                        <IconButton
                          onClick={() => setShowDialogAdd(data)}
                          disabled={
                            (currentProfile.isAllowedToEdit &&
                              !currentProfile.isAllowedToEdit?.directors) ||
                            false
                          }
                          sx={{
                            opacity:
                              currentProfile.isAllowedToEdit &&
                              !currentProfile.isAllowedToEdit?.directors
                                ? "0.5"
                                : "unset",
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            setSelectedStakeholderId(data.stakeholderId);
                            setShowDeleteDialog(true);
                          }}
                          disabled={
                            (currentProfile.isAllowedToEdit &&
                              !currentProfile.isAllowedToEdit?.directors) ||
                            false
                          }
                          sx={{
                            opacity:
                              currentProfile.isAllowedToEdit &&
                              !currentProfile.isAllowedToEdit?.directors
                                ? "0.5"
                                : "unset",
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          color: KYCStatusColor[data.kycStatus],

                          [theme.breakpoints.down("md")]: {
                            display: "none",
                          },
                        }}
                      >
                        {KYCStatusText[data.kycStatus]}
                      </Typography>

                      <Box
                        sx={{
                          display: "none",
                          [theme.breakpoints.down("md")]: {
                            display: "block",
                            marginTop: "1rem",
                          },
                        }}
                      >
                        <BlackTooltip
                          title={KYCStatusText[data.kycStatus]}
                          isMobile={true}
                        >
                          {" "}
                          {data.kycStatus === 0 && (
                            <Unsubscribe
                              sx={{
                                color: KYCStatusColor[data.kycStatus],
                                opacity: "0.6",
                              }}
                            />
                          )}
                          {data.kycStatus === 1 && (
                            <HourglassBottom
                              sx={{
                                color: KYCStatusColor[data.kycStatus],
                              }}
                            />
                          )}
                          {data.kycStatus === 2 && (
                            <Verified
                              sx={{
                                color: KYCStatusColor[data.kycStatus],
                              }}
                            />
                          )}
                          {data.kycStatus === 3 && (
                            <Cancel
                              sx={{
                                color: KYCStatusColor[data.kycStatus],
                              }}
                            />
                          )}
                        </BlackTooltip>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
              <Box mt={3}>
                <AppSecondaryButton
                  title="Add another"
                  className="btn-small ezifin-default-outlined-button"
                  onClick={() => setShowDialogAdd("add")}
                  disabled={
                    (currentProfile.isAllowedToEdit &&
                      !currentProfile.isAllowedToEdit?.directors) ||
                    false
                  }
                />
              </Box>

              <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                <AppPrimaryButton
                  title="Continue"
                  isLoading={false}
                  loadingText="Please wait.."
                  onClick={() => {
                    isExecutantRoleExists();
                  }}
                />
              </Box>
            </>
          )}
        </>
      )}

      {/* DIALOG DONE */}
      <Dialog
        open={showDialogDone}
        fullWidth
        maxWidth="sm"
        onClose={() => {
          setCheckAgreementDirector(false);
          setShowDialogDone(false);
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h2">Almost Done </Typography>
            <IconButton
              onClick={() => {
                setCheckAgreementDirector(false);
                setShowDialogDone(false);
              }}
            >
              <CloseDialogIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ overflow: "hidden" }}>
          <Typography variant="h3" fontWeight={400} fontSize={"15px"}>
            All listed directors will receive an email shortly to review and
            authorise this loan application. Make sure the provided details are
            accurate before proceeding. When you're ready, click confirm to
            continue.
          </Typography>
          {/* <Typography variant="h3" fontSize={"13px"}>
            Sending authorisation email to:
          </Typography> */}
          {currentProfile.stakeholders
            ?.filter((s) => s.kycStatus === 0)
            .map((data, index) => (
              <Box
                key={index}
                mt={3}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: "auto",
                  border: "1px solid #E5E5E5",
                  borderRadius: "8px",
                  padding: "5px 16px",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <Avatar
                    sx={{
                      background:
                        "linear-gradient(257.91deg, #0575E6 -9.32%, #021B79 78.43%, #000000 177.87%)",
                    }}
                  >
                    {data.firstName?.slice(0, 1)}
                  </Avatar>
                  <Box>
                    <Typography variant="h3" fontWeight={400}>
                      {data.firstName} {data.lastName}
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={400}
                      sx={{ marginTop: "-5px" }}
                    >
                      {getRoleLabel(data.role)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex" }}>
                  <IconButton
                    onClick={() => setShowDialogAdd(data)}
                    disabled={
                      (currentProfile.isAllowedToEdit &&
                        !currentProfile.isAllowedToEdit?.directors) ||
                      false
                    }
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      setSelectedStakeholderId(data.stakeholderId);
                      setShowDeleteDialog(true);
                    }}
                    disabled={
                      (currentProfile.isAllowedToEdit &&
                        !currentProfile.isAllowedToEdit?.directors) ||
                      false
                    }
                  >
                    <RemoveIcon />
                  </IconButton>
                </Box>
              </Box>
            ))}
          <Box
            sx={{
              textAlign: "left",
              marginTop: "1.5rem",
              display: "flex",
              gap: "0.5rem",
              alignItems: "flex-start",
            }}
          >
            <Checkbox
              checked={checkAgreementDirector}
              sx={{ position: "relative", top: "-2px" }}
              color="primary"
              onClick={() => setCheckAgreementDirector((prev) => !prev)}
            />
            <Typography
              variant="h4"
              fontWeight={400}
              lineHeight={"20px"}
              sx={{ maxWidth: "90%", textWrap: "balance" }}
            >
              I confirm that, to the best of my knowledge, all relevant
              Directors and Shareholders have been informed of this application
              and have been duly included.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            gap: "12px",
          }}
        >
          <AppSecondaryButton
            title="Cancel"
            className="btn-small ezifin-default-outlined-button"
            onClick={() => setShowDialogDone(false)}
            fullWidth
          />
          <AppPrimaryButton
            title="Confirm"
            className="btn-small"
            isLoading={isInitiating}
            onClick={actionOnConfirmDirectors}
            fullWidth
            disabled={!checkAgreementDirector || isInitiating}
          />
        </DialogActions>
      </Dialog>

      {/* DIALOG ADD AND DELETE*/}
      <Dialog open={showDialogAdd} fullWidth maxWidth="sm">
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h2">
              {showDialogAdd && showDialogAdd !== "add" ? "Edit" : "Add"}{" "}
              shareholder{" "}
            </Typography>
            <IconButton onClick={() => setShowDialogAdd(null)}>
              <CloseDialogIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <HandleAddDirectorsForm
            handleSubmitFormData={handleSubmitForm}
            userInfoData={currentProfile}
            isDialog={true}
            showDialogAdd={showDialogAdd}
          />
        </DialogContent>
      </Dialog>

      {showDeleteDialog && (
        <DeleteConfirmation
          showConfirm={showDeleteDialog}
          onCloseForm={onCloseDeleteDialog}
          deleteDirector={handleDeleteStakeholder}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
};

export default Directors;
